import { LitElement, html, svg, css } from 'lit';

/**
 * CrusherChart
 *
 * A dashboard-grade SVG chart with axes, gridlines, and labels. Fills the gap between
 * `crusher-sparkline` (inline trend, no axes) and a full chart library (Recharts/ECharts).
 * Use this when you need a real chart on a dashboard, report, or analytics page. If you need
 * tooltips-on-hover, zooming, brushing, or more than two series, reach for a chart library;
 * this primitive stays out of that territory, like the sparkline does.
 *
 * Type 'bar' renders vertical bars; 'line' renders a stroked path with optional area fill.
 *
 * Properties:
 *   - type        (enum), "bar" (default) | "line"
 *   - data        (Array of numbers), the values to plot. Empty renders a flat baseline.
 *   - labels      (Array of strings, optional, same length as data), x-axis labels
 *   - height      (number), SVG viewport height (default 220)
 *   - tone        (enum), "brand" | "success" | "warning" | "danger" | "info" | "neutral" (default "brand")
 *   - show-area   (boolean, line only), soft fill under the line
 *   - show-grid   (boolean), render y gridlines + value labels (default true)
 *   - max         (number, optional), override the auto-computed nice max
 *   - aria-label  (string), overrides the auto-derived accessible name
 *
 * A11y: <svg role="img"> with an auto-derived summary (type, n, min, max, last, delta).
 *
 * No events; display primitive. Token-driven; respects dark mode + density via CSS vars.
 */
const TONE_VARS = {
  brand: 'var(--crusher-color-brand-primary)',
  success: 'var(--crusher-color-status-success)',
  warning: 'var(--crusher-color-status-warning)',
  danger: 'var(--crusher-color-status-failure)',
  info: 'var(--crusher-color-status-info)',
  neutral: 'var(--crusher-color-status-neutral)',
};

// Round a max up to a "nice" number (1, 2, 5 x 10^n) for clean gridlines.
function niceMax(value) {
  if (value <= 0) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = Math.pow(10, exp);
  const f = value / base;
  let nice;
  if (f <= 1) nice = 1;
  else if (f <= 2) nice = 2;
  else if (f <= 5) nice = 5;
  else nice = 10;
  return nice * base;
}

export class CrusherChart extends LitElement {
  static properties = {
    type:       { type: String, reflect: true },
    data:       { type: Array, converter: { fromAttribute: (v) => { try { return v ? JSON.parse(v) : []; } catch { return []; } } } },
    labels:     { type: Array, converter: { fromAttribute: (v) => { try { return v ? JSON.parse(v) : []; } catch { return []; } } } },
    height:     { type: Number, reflect: true },
    tone:       { type: String, reflect: true },
    showArea:   { type: Boolean, attribute: 'show-area', reflect: true },
    showGrid:   { type: Boolean, attribute: 'show-grid', reflect: true },
    max:        { type: Number, reflect: true },
  };

  constructor() {
    super();
    this.type = 'bar';
    this.data = [];
    this.labels = [];
    this.height = 220;
    this.tone = 'brand';
    this.showArea = false;
    this.showGrid = true;
    this.max = 0;
  }

  static styles = css`
    :host { display: block; width: 100%; color: var(--crusher-color-text-primary); }
    svg { display: block; width: 100%; height: auto; overflow: visible; }
    .grid line { stroke: var(--crusher-color-border-primary); stroke-dasharray: 2 4; }
    .grid text, .x-labels text {
      font-family: var(--crusher-font-family-mono, monospace);
      font-size: 10px;
      fill: var(--crusher-color-text-secondary);
    }
    .bar { transition: opacity 0.15s ease; }
    .bar:hover { opacity: 0.78; }
  `;

  render() {
    const data = (this.data && this.data.length) ? this.data : [0];
    const n = data.length;
    const rawMax = this.max > 0 ? this.max : Math.max(...data, 1);
    const top = this.max > 0 ? this.max : niceMax(rawMax);
    const W = 600;            // viewBox width; SVG scales to container
    const H = this.height;
    const padL = 40, padR = 12, padT = 12, padB = 26;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const color = TONE_VARS[this.tone] || TONE_VARS.brand;
    const x = (i) => padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
    const y = (v) => padT + plotH - (top === 0 ? 0 : (v / top) * plotH);

    // 4 gridlines
    const grids = [];
    if (this.showGrid) {
      for (let g = 0; g <= 4; g++) {
        const val = (top / 4) * g;
        const gy = y(val);
        grids.push(svg`
          <g class="grid">
            <line x1=${padL} y1=${gy} x2=${W - padR} y2=${gy} />
            <text x=${padL - 6} y=${gy + 3} text-anchor="end">${this._fmt(val)}</text>
          </g>`);
      }
    }

    // bars
    let shapes;
    if (this.type === 'line') {
      const pts = data.map((v, i) => `${x(i)},${y(v)}`).join(' ');
      const areaPath = data.length
        ? `M ${x(0)},${y(0)} L ${pts.replaceAll(' ', ' L ')} L ${x(n - 1)},${y(0)} Z`
        : '';
      shapes = html`
        ${this.showArea && data.length > 1
          ? svg`<path d=${areaPath} fill=${color} opacity="0.14" />`
          : null}
        <polyline points=${pts} fill="none" stroke=${color} stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round" />
        ${data.map((v, i) => svg`<circle cx=${x(i)} cy=${y(v)} r="2.5" fill=${color} />`)}
      `;
    } else {
      const bw = n > 0 ? Math.min(48, (plotW / n) * 0.62) : 0;
      shapes = data.map((v, i) => {
        const bx = x(i) - bw / 2;
        const by = y(v);
        const bh = padT + plotH - by;
        return svg`<rect class="bar" x=${bx} y=${by} width=${bw} height=${Math.max(0, bh)} rx="3" fill=${color} />`;
      });
    }

    // x labels
    const xlabels = (this.labels && this.labels.length === n)
      ? this.labels.map((lab, i) => svg`<text x=${x(i)} y=${H - 8} text-anchor="middle">${lab}</text>`)
      : [];

    return html`<svg viewBox="0 0 ${W} ${H}" role="img" aria-label=${this._aria(data)}>
      ${grids}
      <g class="x-labels">${xlabels}</g>
      ${shapes}
    </svg>`;
  }

  _fmt(v) {
    if (v >= 1000) return (v / 1000).toFixed(v >= 10000 ? 0 : 1) + 'k';
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
  }

  _aria(data) {
    if (!data || !data.length) return 'empty chart';
    const min = Math.min(...data);
    const max = Math.max(...data);
    const last = data[data.length - 1];
    const first = data[0];
    const delta = first ? (((last - first) / Math.abs(first)) * 100).toFixed(0) : 0;
    return `${this.type} chart, ${data.length} points, min ${this._fmt(min)}, max ${this._fmt(max)}, last ${this._fmt(last)}, ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta)}%`;
  }
}

customElements.define('crusher-chart', CrusherChart);
export default CrusherChart;
